import type React from "react";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { OverflowTooltip } from "app/pages/sales-intelligence/common-components/OverflowTooltip/OverflowTooltip";
import { ContactSource } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import {
    StyledContactCard,
    StyledContactCardsContainer,
} from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/styles";

const SOURCE_CONFIG: Record<ContactSource, { icon: IconName; labelKey: string }> = {
    [ContactSource.Salesforce]: {
        icon: "salesforce",
        labelKey: "si.pages.ai_agent.form.keyAttendees.source.salesforce",
    },
    [ContactSource.Hubspot]: { icon: "hubspot", labelKey: "si.pages.ai_agent.form.keyAttendees.source.hubspot" },
    [ContactSource.Dynamics]: { icon: "dynamics", labelKey: "si.pages.ai_agent.form.keyAttendees.source.dynamis" },
    [ContactSource.Similarweb]: {
        icon: "similar-icon",
        labelKey: "si.pages.ai_agent.form.keyAttendees.source.similarweb",
    },
};

const getContactSourceIconColor = (isDisabled: boolean, source: ContactSource) => {
    if (isDisabled) {
        return colorsPalettes.carbon[200];
    }
    return source === ContactSource.Similarweb ? undefined : colorsPalettes.carbon[300];
};

type Props = {
    onSelect: (contact: Contact) => void;
    value: Contact[];
    isDisabled: boolean;
    isLoading: boolean;
};

export const ContactCards: React.FC<Props> = ({ onSelect, value, isLoading, isDisabled }) => {
    const handleClick = (contact: Contact) => {
        if (isDisabled) {
            return;
        }
        onSelect(contact);
    };

    if (value.length === 0 && !isDisabled && !isLoading) {
        return (
            <Box
                width="100%"
                margin="0 0 12px 0"
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("si.pages.ai_agent.form.keyAttendees.search.no_results")} <br />
                    {translate("si.pages.ai_agent.form.keyAttendees.search.no_results.second")}
                </Text>
            </Box>
        );
    }

    if (value.length === 0 && isDisabled && !isLoading) {
        return (
            <Box>
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[250]}
                >
                    {translate("si.pages.ai_agent.form.keyAttendees.search.no_results_disabled")}
                </Text>
            </Box>
        );
    }

    return (
        <StyledContactCardsContainer>
            {value.map((contact) => (
                <StyledContactCard
                    key={contact.id}
                    onClick={() => handleClick(contact)}
                    isDisabled={isDisabled}
                >
                    <Checkbox
                        selected={contact.isSelected}
                        isDisabled={isDisabled}
                    />
                    <Box
                        maxWidth="100%"
                        overflow="hidden"
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap="4px"
                        >
                            <OverflowTooltip
                                fontVariant="body2"
                                text={contact.name ?? ""}
                                color={isDisabled ? colorsPalettes.midnight[100] : colorsPalettes.carbon[500]}
                                disabled={false}
                            />
                            {contact.source ? (
                                <Tooltip
                                    content={translate(SOURCE_CONFIG[contact.source].labelKey)}
                                    placement="top"
                                >
                                    <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                        width="18px"
                                        height="18px"
                                        borderRadius="50%"
                                        background={colorsPalettes.sky[100]}
                                        aria-label={translate(SOURCE_CONFIG[contact.source].labelKey)}
                                    >
                                        <SWReactIcons
                                            iconName={SOURCE_CONFIG[contact.source].icon}
                                            iconColor={getContactSourceIconColor(isDisabled, contact.source)}
                                            size="11px"
                                        />
                                    </Flex>
                                </Tooltip>
                            ) : null}
                        </Box>
                        <OverflowTooltip
                            fontVariant="caption"
                            text={contact.title ?? ""}
                            color={isDisabled ? colorsPalettes.midnight[100] : colorsPalettes.carbon[300]}
                            disabled={false}
                        />
                    </Box>
                </StyledContactCard>
            ))}
        </StyledContactCardsContainer>
    );
};
